import flatMap from 'lodash/flatMap'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import omit from 'lodash/omit'
import some from 'lodash/some'
import type {
  PivotDateGroupEnum,
  PivotDateRange,
  PivotField,
  PivotLayout,
  PivotLayoutAggregatedMeasure,
  PivotLayoutColumnTarget,
  PivotLayoutColumnWidth,
  PivotLayoutField,
  PivotLayoutFilter,
  PivotLayoutMeasure,
  PivotLayoutMemberSort,
  PivotLayoutValueColumn,
  PivotLayoutValueFilter,
  PivotMeasureFormat,
} from '@ztechapps/common.contract'
import { DirectionEnum, PivotAggregationEnum, PivotFieldTypeEnum } from '@ztechapps/common.contract'
import { findPivotValueFilterProblem, isPivotCalculatedMeasure } from '@ztechapps/common.utils'
import { PIVOT_AVERAGE_DECIMAL_PLACES, PIVOT_DEFAULT_MEASURE_FORMAT } from './constants'
import { PivotZoneEnum } from './PivotZoneEnum'
import { toPivotDefaultAggregation } from './pivotAggregation.utils'
import { isSamePivotValueColumn } from './pivotValueColumn.utils'
import { getPivotZoneItems, setPivotZoneItems } from './pivotZoneItems.utils'

/** Adds a field to a pivot layout zone. */
export const addPivotLayoutItem = (
  layout: PivotLayout,
  zone: PivotZoneEnum,
  field: PivotField,
  dateGroup?: PivotDateGroupEnum
): PivotLayout => {
  if (zone === PivotZoneEnum.Measures) {
    const aggregation = toPivotDefaultAggregation(field)
    if (
      layout.measures.some(
        (item) => !isPivotCalculatedMeasure(item) && item.field === field.name && item.aggregation === aggregation
      )
    )
      return layout
    return { ...layout, measures: [...layout.measures, { field: field.name, aggregation }] }
  }
  const items = getPivotZoneItems(layout, zone) as PivotLayoutField[]
  if (items.some((item) => item.field === field.name && item.dateGroup === dateGroup)) return layout
  const item = dateGroup ? { field: field.name, dateGroup } : { field: field.name }
  return setPivotZoneItems(layout, zone, [...items, item])
}

/** Checks a field into the layout — a number field aggregates in Values, anything else groups in Rows. */
export const checkPivotLayoutField = (
  layout: PivotLayout,
  field: PivotField,
  dateGroup?: PivotDateGroupEnum
): PivotLayout => {
  if (field.type === PivotFieldTypeEnum.Number) return addPivotLayoutItem(layout, PivotZoneEnum.Measures, field)
  return addPivotLayoutItem(layout, PivotZoneEnum.Rows, field, dateGroup)
}

/** Unchecks a field out of the layout — date fields keep their Values aggregations. */
export const uncheckPivotLayoutField = (
  layout: PivotLayout,
  field: PivotField,
  dateGroup?: PivotDateGroupEnum
): PivotLayout => {
  const isMatch = (item: PivotLayoutField): boolean => item.field === field.name && item.dateGroup === dateGroup
  const measures =
    field.type === PivotFieldTypeEnum.Date
      ? layout.measures
      : layout.measures.filter((item) => isPivotCalculatedMeasure(item) || item.field !== field.name)
  return prunePivotValueFilters({
    ...layout,
    rows: layout.rows.filter((item) => !isMatch(item)),
    columns: layout.columns.filter((item) => !isMatch(item)),
    filters: layout.filters.filter((item) => !isMatch(item)),
    measures,
  })
}

/** Whether a filters entry holds a member selection, a date grouping or a day range — anything besides a value filter. */
export const hasPivotFilterSelection = (filter: PivotLayoutFilter): boolean =>
  !isEmpty(filter.includedValues) ||
  !isEmpty(filter.excludedValues) ||
  filter.dateGroup !== undefined ||
  filter.dateRange !== undefined

/** A filters entry without its value filter — as a one-item list, or empty when nothing else is left on it. */
const withoutValueFilter = (filter: PivotLayoutFilter): PivotLayoutFilter[] => {
  const rest = omit(filter, 'valueFilter')
  return hasPivotFilterSelection(rest) ? [rest] : []
}

/**
 * Removes the value filter of every filters entry that breaks a rule against the layout — a ranked field that left
 * Rows and Columns, a ranking measure that left Values, a member selection beside it. An entry left with nothing is
 * dropped too. Returns the same layout object when no value filter is invalid.
 */
export const prunePivotValueFilters = (layout: PivotLayout): PivotLayout => {
  const isInvalid = (filter: PivotLayoutFilter): boolean =>
    filter.valueFilter !== undefined && findPivotValueFilterProblem(layout, filter) !== undefined
  if (!some(layout.filters, isInvalid)) return layout
  return {
    ...layout,
    filters: flatMap(layout.filters, (filter) => (isInvalid(filter) ? withoutValueFilter(filter) : [filter])),
  }
}

/** Sets the aggregation of the Values item at an index, keeping its format. */
export const setPivotMeasureAggregation = (
  layout: PivotLayout,
  index: number,
  aggregation: PivotAggregationEnum
): PivotLayout => {
  const current = layout.measures[index]
  if (!current || isPivotCalculatedMeasure(current)) return layout
  const updated: PivotLayoutAggregatedMeasure = { ...current, aggregation }
  if (
    layout.measures.some(
      (item, i) =>
        i !== index &&
        !isPivotCalculatedMeasure(item) &&
        item.field === updated.field &&
        item.aggregation === updated.aggregation
    )
  )
    return layout
  return prunePivotValueFilters({
    ...layout,
    measures: map(layout.measures, (item, i) => (i === index ? updated : item)),
  })
}

/** Moves a pivot layout item between zones. */
export const movePivotLayoutItem = (
  layout: PivotLayout,
  from: PivotZoneEnum,
  index: number,
  to: PivotZoneEnum,
  fields: PivotField[]
): PivotLayout => {
  if (from === to) return layout
  const moving = getPivotZoneItems(layout, from)[index]
  if (!moving) return layout
  if (to === PivotZoneEnum.Measures) {
    const aggregation = toPivotDefaultAggregation(fields.find((field) => field.name === moving.field))
    if (
      layout.measures.some(
        (item) => !isPivotCalculatedMeasure(item) && item.field === moving.field && item.aggregation === aggregation
      )
    )
      return layout
    const removed = removePivotZoneItem(layout, from, index)
    return prunePivotValueFilters({ ...removed, measures: [...removed.measures, { field: moving.field, aggregation }] })
  }
  const dateGroup = (moving as PivotLayoutField).dateGroup
  const moved: PivotLayoutField = dateGroup !== undefined ? { field: moving.field, dateGroup } : { field: moving.field }
  const removed = removePivotZoneItem(layout, from, index)
  // a value-only entry of the ranked field would read as that field already sitting in Report filters
  const base = to === PivotZoneEnum.Filters ? prunePivotValueFilters(removed) : removed
  const target = getPivotZoneItems(base, to) as PivotLayoutField[]
  if (target.some((item) => item.field === moved.field && item.dateGroup === moved.dateGroup)) return layout
  // pruned only once the field sits in its new zone, so a ranked field moved between Rows and Columns keeps its filter
  return prunePivotValueFilters(setPivotZoneItems(base, to, [...target, moved]))
}

const removePivotZoneItem = (layout: PivotLayout, zone: PivotZoneEnum, index: number): PivotLayout =>
  setPivotZoneItems(
    layout,
    zone,
    getPivotZoneItems(layout, zone).filter((_, i) => i !== index)
  )

/** Removes a pivot layout item from a zone, dropping the value filters it leaves invalid. */
export const removePivotLayoutItem = (layout: PivotLayout, zone: PivotZoneEnum, index: number): PivotLayout =>
  prunePivotValueFilters(removePivotZoneItem(layout, zone, index))

/** Finds the layout item of a field and its date grouping. */
export const findPivotLayoutItem = <TItem extends PivotLayoutField>(
  items: TItem[],
  field: PivotLayoutField
): TItem | undefined => items.find((item) => item.field === field.field && item.dateGroup === field.dateGroup)

/** Sets a filter's excluded values and day range, replacing the item of the same field and grouping. */
export const setPivotLayoutFilter = (
  layout: PivotLayout,
  field: PivotLayoutField,
  excludedValues: string[],
  dateRange?: PivotDateRange
): PivotLayout => {
  const next: PivotLayoutFilter = { field: field.field, excludedValues }
  if (field.dateGroup !== undefined) next.dateGroup = field.dateGroup
  if (dateRange !== undefined) next.dateRange = dateRange
  const current = findPivotLayoutItem(layout.filters, field)
  if (!current) return { ...layout, filters: [...layout.filters, next] }
  return { ...layout, filters: layout.filters.map((item) => (item === current ? next : item)) }
}

/**
 * Sets the Top / Bottom N of a Rows or Columns field, replacing its member selection, or removes it with null —
 * the entry too when nothing else is left on it.
 */
export const setPivotLayoutValueFilter = (
  layout: PivotLayout,
  field: string | PivotLayoutField,
  valueFilter: PivotLayoutValueFilter | null
): PivotLayout => {
  const target: PivotLayoutField = typeof field === 'string' ? { field } : field
  const current = findPivotLayoutItem(layout.filters, target)
  if (valueFilter === null) {
    if (!current) return layout
    return {
      ...layout,
      filters: flatMap(layout.filters, (item) => (item !== current ? [item] : withoutValueFilter(item))),
    }
  }
  const next: PivotLayoutFilter = {
    ...omit(current, ['includedValues', 'excludedValues']),
    field: target.field,
    valueFilter,
  }
  if (target.dateGroup !== undefined) next.dateGroup = target.dateGroup
  if (!current) return { ...layout, filters: [...layout.filters, next] }
  return { ...layout, filters: map(layout.filters, (item) => (item === current ? next : item)) }
}

/** Sets a Rows or Columns field's member sort, removing its entry when direction is unset. */
export const setPivotMemberSort = (
  layout: PivotLayout,
  field: PivotLayoutField,
  direction?: DirectionEnum
): PivotLayout => {
  const current = findPivotLayoutItem(layout.memberSorts, field)
  const memberSorts = layout.memberSorts.filter((item) => item !== current)
  if (direction !== undefined) {
    const next: PivotLayoutMemberSort = { field: field.field, direction }
    if (field.dateGroup !== undefined) next.dateGroup = field.dateGroup
    memberSorts.push(next)
  }
  // a Rows member order replaces row-by-value order; a Columns one keeps it
  if (findPivotLayoutItem(layout.rows, field)) return { ...omit(layout, 'valueSort'), memberSorts }
  return { ...layout, memberSorts }
}

/** Cycles a value column's sort: unset to descending, descending to ascending, ascending to removed. */
export const cyclePivotValueSort = (layout: PivotLayout, valueColumn: PivotLayoutValueColumn): PivotLayout => {
  if (layout.valueSort && isSamePivotValueColumn(layout.valueSort, valueColumn)) {
    if (layout.valueSort.direction === DirectionEnum.desc)
      return { ...layout, valueSort: { ...valueColumn, direction: DirectionEnum.asc } }
    return omit(layout, 'valueSort')
  }
  return { ...layout, valueSort: { ...valueColumn, direction: DirectionEnum.desc } }
}

/** Finds the stored width of a column target. */
export const findPivotColumnWidth = (
  columnWidths: PivotLayoutColumnWidth[],
  target: PivotLayoutColumnTarget
): PivotLayoutColumnWidth | undefined =>
  'rowField' in target
    ? columnWidths.find(
        (entry) =>
          'rowField' in entry &&
          entry.rowField.field === target.rowField.field &&
          entry.rowField.dateGroup === target.rowField.dateGroup
      )
    : columnWidths.find(
        (entry) => 'valueColumn' in entry && isSamePivotValueColumn(entry.valueColumn, target.valueColumn)
      )

/** Stores a column width, removing its entry when width is unset. */
export const setPivotColumnWidth = (
  layout: PivotLayout,
  target: PivotLayoutColumnTarget,
  width?: number
): PivotLayout => {
  const current = findPivotColumnWidth(layout.columnWidths, target)
  const columnWidths = layout.columnWidths.filter((entry) => entry !== current)
  if (width !== undefined) columnWidths.push({ ...target, width })
  return { ...layout, columnWidths }
}

/** Resolves a measure's format with defaults filled in. */
export const resolvePivotMeasureFormat = (measure: PivotLayoutMeasure): Required<PivotMeasureFormat> => {
  const format = measure.format ?? {}
  return {
    decimalPlaces:
      format.decimalPlaces ??
      (isPivotCalculatedMeasure(measure) || measure.aggregation === PivotAggregationEnum.Average
        ? PIVOT_AVERAGE_DECIMAL_PLACES
        : PIVOT_DEFAULT_MEASURE_FORMAT.decimalPlaces),
    currencySymbol: format.currencySymbol ?? PIVOT_DEFAULT_MEASURE_FORMAT.currencySymbol,
    thousandsSeparator: format.thousandsSeparator ?? PIVOT_DEFAULT_MEASURE_FORMAT.thousandsSeparator,
    decimalSeparator: format.decimalSeparator ?? PIVOT_DEFAULT_MEASURE_FORMAT.decimalSeparator,
    negativeNumberFormat: format.negativeNumberFormat ?? PIVOT_DEFAULT_MEASURE_FORMAT.negativeNumberFormat,
    isPercent: format.isPercent ?? PIVOT_DEFAULT_MEASURE_FORMAT.isPercent,
    textAlign: format.textAlign ?? PIVOT_DEFAULT_MEASURE_FORMAT.textAlign,
    nullValue: format.nullValue ?? PIVOT_DEFAULT_MEASURE_FORMAT.nullValue,
  }
}
